import React, { useState, useEffect } from 'react';
import Button from '../shared/Button';
import Modal from '../shared/Modal';
import ConfirmDialog from '../shared/ConfirmDialog';
import { useToast } from '../../hooks/useToast';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import { LOADING_CALCULATION_BASIS, LOADING_CALCULATION_LABELS } from '../../constants/systemConfig';
import {
    getLoadingRules,
    createLoadingRuleConfig,
    updateLoadingRuleConfig,
    deleteLoadingRuleConfig
} from '../../services/firestore/systemConfigService';
import { getVehicleTypes } from '../../services/business/vehicleTypeService';

const LoadingRuleConfig = ({ user }) => {
    const { success, error } = useToast();
    const { confirmConfig, showConfirm, closeConfirm } = useConfirmDialog();
    const [loading, setLoading] = useState(false);
    const [loadingRules, setLoadingRules] = useState([]);
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({
        tenantId: user?.tenantId,
        vehicleTypeCode: '',
        vehicleTypeName: '',
        calculationBasis: LOADING_CALCULATION_BASIS.WEIGHT,
        calculationBasis: LOADING_CALCULATION_BASIS.WEIGHT,
        loadingDurationPerUnit: 0,
        baseLoadTime: 15
    });

    useEffect(() => {
        if (user?.tenantId) {
            loadData();
        }
    }, [user?.tenantId]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [rulesData, typesData] = await Promise.all([
                getLoadingRules(user.tenantId, false),
                getVehicleTypes(user.tenantId)
            ]);
            setLoadingRules(rulesData);
            setVehicleTypes(typesData);
        } catch (err) {
            error('Veriler yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (item = null) => {
        if (item) {
            setEditingItem(item);
            setFormData(item);
        } else {
            setEditingItem(null);
            setFormData({
                tenantId: user.tenantId,
                vehicleTypeCode: '',
                vehicleTypeName: '',
                calculationBasis: LOADING_CALCULATION_BASIS.WEIGHT,
                calculationBasis: LOADING_CALCULATION_BASIS.WEIGHT,
                loadingDurationPerUnit: 0,
                baseLoadTime: 15
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingItem(null);
    };

    const handleVehicleTypeChange = (code) => {
        const selectedType = vehicleTypes.find(t => t.code === code);
        setFormData({
            ...formData,
            vehicleTypeCode: code,
            vehicleTypeName: selectedType?.name || ''
        });
    };

    const handleSave = async () => {
        try {
            if (!formData.vehicleTypeCode) {
                error('Lütfen araç tipi seçin');
                return;
            }

            if (formData.loadingDurationPerUnit <= 0) {
                error('Birim süresi 0\'dan büyük olmalı');
                return;
            }

            if (editingItem) {
                await updateLoadingRuleConfig(editingItem.id, formData);
                success('Yükleme kuralı güncellendi');
            } else {
                await createLoadingRuleConfig(formData);
                success('Yükleme kuralı oluşturuldu');
            }

            handleCloseModal();
            loadData();
        } catch (err) {
            error('İşlem başarısız: ' + err.message);
        }
    };

    const handleDelete = async (id) => {
        showConfirm({
            title: 'Yükleme Kuralını Sil',
            message: 'Bu yükleme kuralını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.',
            type: 'error',
            confirmText: 'Evet, Sil',
            onConfirm: async () => {
                try {
                    await deleteLoadingRuleConfig(id);
                    success('Yükleme kuralı silindi');
                    loadData();
                } catch (err) {
                    error('Silme başarısız: ' + err.message);
                }
            }
        });
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '40px' }}>
                <div className="badge badge-pending">Yükleniyor...</div>
            </div>
        );
    }

    return (
        <div style={{ animation: 'none' }}>
            <div className="flex justify-between items-center mb-lg">
                <div>
                    <h3 className="mb-xs">Yükleme Süresi Kuralları</h3>
                    <p className="text-secondary text-sm">
                        Araç tiplerine göre yükleme süresi hesaplama parametrelerini tanımlayın
                    </p>
                </div>
                <Button variant="primary" onClick={() => handleOpenModal()}>
                    + Yeni Kural
                </Button>
            </div>

            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                {loadingRules.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                        <div style={{ fontSize: '2rem', marginBottom: '12px' }}>⚙️</div>
                        Henüz yükleme kuralı tanımlanmamış.
                    </div>
                ) : (
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Araç Tipi</th>
                                <th>Hesaplama Temeli</th>
                                <th>Birim/Dakika</th>
                                <th>Minimum Süre (dk)</th>
                                <th>Durum</th>
                                <th>İşlemler</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loadingRules.map(rule => (
                                <tr key={rule.id}>
                                    <td style={{ fontWeight: 'var(--font-bold)' }}>{rule.vehicleTypeName}</td>
                                    <td>
                                        <span className="badge badge-alternative">
                                            {LOADING_CALCULATION_LABELS[rule.calculationBasis]}
                                        </span>
                                    </td>
                                    <td>
                                        <strong>{rule.loadingDurationPerUnit || rule.unitsPerMinute}</strong> {
                                            rule.calculationBasis === LOADING_CALCULATION_BASIS.WEIGHT ? 'dk/Ton' : 'dk/m³'
                                        }
                                    </td>
                                    <td>{rule.baseLoadTime} dk</td>
                                    <td>
                                        <span className={`badge ${rule.isActive ? 'badge-completed' : 'badge-pending'}`}>
                                            {rule.isActive ? 'Aktif' : 'Pasif'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="flex gap-sm">
                                            <Button size="sm" variant="secondary" onClick={() => handleOpenModal(rule)}>
                                                Düzenle
                                            </Button>
                                            <Button size="sm" variant="danger" onClick={() => handleDelete(rule.id)}>
                                                Sil
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <Modal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title={editingItem ? 'Yükleme Kuralı Düzenle' : 'Yeni Yükleme Kuralı'}
            >
                <div className="form-group">
                    <label className="form-label">Araç Tipi *</label>
                    <select
                        className="select"
                        value={formData.vehicleTypeCode}
                        onChange={e => handleVehicleTypeChange(e.target.value)}
                        disabled={editingItem !== null}
                    >
                        <option value="">Seçiniz...</option>
                        {vehicleTypes.map(type => (
                            <option key={type.id} value={type.code}>{type.name}</option>
                        ))}
                    </select>
                    {editingItem && (
                        <small className="text-secondary">Araç tipi düzenlenemez. Yeni kural oluşturun.</small>
                    )}
                </div>

                <div className="form-group">
                    <label className="form-label">Hesaplama Temeli *</label>
                    <select
                        className="select"
                        value={formData.calculationBasis}
                        onChange={e => setFormData({ ...formData, calculationBasis: e.target.value })}
                    >
                        <option value={LOADING_CALCULATION_BASIS.WEIGHT}>
                            {LOADING_CALCULATION_LABELS[LOADING_CALCULATION_BASIS.WEIGHT]}
                        </option>
                        <option value={LOADING_CALCULATION_BASIS.VOLUME}>
                            {LOADING_CALCULATION_LABELS[LOADING_CALCULATION_BASIS.VOLUME]}
                        </option>
                    </select>
                    <small className="text-secondary">
                        Yükleme süresi hesaplamasında hangi değişkenin kullanılacağını seçin
                    </small>
                </div>

                <div className="form-group">
                    <label className="form-label">
                        Birim Süresi (Dk) * {formData.calculationBasis === LOADING_CALCULATION_BASIS.WEIGHT ? '(Dk/Ton)' : '(Dk/m³)'}
                    </label>
                    <input
                        type="number"
                        className="input"
                        min="0.1"
                        step="0.1"
                        value={formData.loadingDurationPerUnit || ''}
                        onChange={e => setFormData({ ...formData, loadingDurationPerUnit: parseFloat(e.target.value) || 0 })}
                    />
                    <small className="text-secondary">
                        1 birim {formData.calculationBasis === LOADING_CALCULATION_BASIS.WEIGHT ? 'ton' : 'm³'} yüklemek kaç dakika sürer?
                    </small>
                </div>

                <div className="form-group">
                    <label className="form-label">Minimum Yükleme Süresi (dakika) *</label>
                    <input
                        type="number"
                        className="input"
                        min="0"
                        value={formData.baseLoadTime}
                        onChange={e => {
                            const val = parseInt(e.target.value);
                            setFormData({ ...formData, baseLoadTime: isNaN(val) ? 0 : val });
                        }}
                    />
                    <small className="text-secondary">
                        Yük miktarı ne olursa olsun minimum yükleme süresi
                    </small>
                </div>

                <div className="flex gap-md justify-end mt-lg">
                    <Button variant="secondary" onClick={handleCloseModal}>İptal</Button>
                    <Button variant="primary" onClick={handleSave}>Kaydet</Button>
                </div>
            </Modal>

            <ConfirmDialog
                isOpen={confirmConfig.isOpen}
                onClose={closeConfirm}
                onConfirm={confirmConfig.onConfirm}
                title={confirmConfig.title}
                message={confirmConfig.message}
                type={confirmConfig.type}
                confirmText={confirmConfig.confirmText}
                cancelText={confirmConfig.cancelText}
            />
        </div>
    );
};

export default LoadingRuleConfig;
